import { IonContentCustomEvent } from "@ionic/core";
import { IonContent, IonPage, ScrollDetail } from "@ionic/react";
import debounce from "debounce-promise";
import { ReactNode, RefObject, useCallback, useContext, useEffect, useRef } from "react";
import { useIntl } from "react-intl";

import MenuItem from "@components/menus/MenuItem";
import styles from "@components/pageTypes/AppPage.module.css";
import { scrollPositionContext } from "@context/Contexts";
import useMyClient from "@hooks/useMyClient";
import { clientMenuProps } from "@utils/menuUtils";

type Props = {
  children: ReactNode;
  contentRef?: RefObject<HTMLIonContentElement>;
  gradient?: boolean;
  hideMenuOnMobile?: boolean;
  journeyId: string;
};

const AppPageWithMenu = ({ children, contentRef, gradient = false, hideMenuOnMobile = false, journeyId }: Props) => {
  const { scrollPositionState, setContentRef, setTopPosition } = useContext(scrollPositionContext);
  const queryParams = new URLSearchParams(location.search);

  const defaultContentRef = useRef<HTMLIonContentElement>(null);
  const intl = useIntl();
  const myClient = useMyClient();

  const chosenContentRef = contentRef ?? defaultContentRef;

  useEffect(() => {
    if (chosenContentRef) {
      setContentRef(chosenContentRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenContentRef]);

  const handleSetPositionTop = useCallback(
    (e: IonContentCustomEvent<ScrollDetail>) => {
      if (scrollPositionState.currentType && scrollPositionState.currentRef) {
        setTopPosition(e.detail.scrollTop);
      }
    },
    [scrollPositionState.currentRef, scrollPositionState.currentType, setTopPosition]
  );

  const debouncedSetTopPosition = debounce(handleSetPositionTop, 200);

  const hideMenu = queryParams.has("hideMenu");

  return (
    <IonPage className={gradient ? styles.pageGradient : ""}>
      <IonContent onIonScroll={debouncedSetTopPosition} ref={chosenContentRef} scrollEvents>
        <div className={styles.messagingSpacing}>{children}</div>
      </IonContent>
      <div className={`${hideMenu ? "ion-hide" : styles.tabBar} ${hideMenuOnMobile ? "ion-hide-lg-down" : ""}`}>
        {clientMenuProps(intl, journeyId, myClient, undefined)
          .filter(v => !v.skipMobile)
          .map(prop => (
            <MenuItem {...prop} key={prop.text} />
          ))}
      </div>
    </IonPage>
  );
};

export default AppPageWithMenu;
